import { getApiConfig, DEFAULT_HEADERS } from './config';
import type { ApiError } from '../../types/institution';

// Generic API Response type
export interface ApiResponse<T = any> {
  data: T;
  status: number;
  headers: Record<string, string>;
}

// Request options interface
export interface RequestOptions {
  headers?: Record<string, string>;
  timeout?: number;
  retries?: number;
  retryDelay?: number;
}

// Custom error class for API errors
export class ApiClientError extends Error {
  public status: number;
  public code?: string;
  public details?: Record<string, any>;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiClientError';
    this.status = error.status;
    this.code = error.code;
    this.details = error.details;
  }
}

// Utility function to handle fetch with timeout
const fetchWithTimeout = async (
  url: string,
  options: RequestInit,
  timeout: number
): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

// Utility function to handle retries with exponential backoff
const withRetry = async <T>(
  operation: () => Promise<T>,
  retries: number,
  delay: number
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    if (retries > 0 && error instanceof ApiClientError && error.status >= 500) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return withRetry(operation, retries - 1, delay * 2);
    }
    throw error;
  }
};

// Main API Client class
export class ApiClient {
  private baseUrl: string;
  private defaultOptions: RequestOptions;

  constructor() {
    const config = getApiConfig();
    this.baseUrl = config.BASE_URL;
    this.defaultOptions = {
      timeout: config.TIMEOUT,
      retries: config.RETRY_ATTEMPTS,
      retryDelay: config.RETRY_DELAY,
    };
  }

  // Generic request method
  private async request<T>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
    body?: any,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const mergedOptions = { ...this.defaultOptions, ...options };
    
    // Get auth token for API protection - REQUIRED for all API calls
    const { getAuthToken } = await import('../auth/authService');
    const authToken = await getAuthToken();
    
    // Ensure we have a token before making any API call
    if (!authToken) {
      throw new ApiClientError({
        message: 'Authentication token not available. Please wait for authentication to complete.',
        status: 401,
        code: 'AUTH_TOKEN_REQUIRED',
        details: { endpoint, method }
      });
    }
    
    // Build headers
    const headers: HeadersInit = {
      ...mergedOptions.headers,
      'Authorization': `Bearer ${authToken}`,
    };

    // Only add Content-Type header for requests with body (POST, PUT, etc.)
    if (body && method !== 'GET') {
      headers['Content-Type'] = 'application/json';
      headers['Accept'] = 'application/json';
    } else {
      // For GET requests, only add Accept header to avoid CORS preflight
      headers['Accept'] = 'application/json';
    }

    const requestOptions: RequestInit = {
      method,
      headers,
    };

    // Add body for POST/PUT requests
    if (body && method !== 'GET') {
      requestOptions.body = JSON.stringify(body);
    }

    const operation = async (): Promise<ApiResponse<T>> => {
      try {
        const response = await fetchWithTimeout(
          url,
          requestOptions,
          mergedOptions.timeout || this.defaultOptions.timeout!
        );

        // Convert headers to plain object
        const headers: Record<string, string> = {};
        response.headers.forEach((value, key) => {
          headers[key] = value;
        });

        if (!response.ok) {
          let errorData: any;
          try {
            errorData = await response.json();
          } catch {
            errorData = { message: response.statusText };
          }

          throw new ApiClientError({
            message: errorData.message || `HTTP ${response.status}: ${response.statusText}`,
            status: response.status,
            code: errorData.code,
            details: errorData,
          });
        }

        let data: T;
        try {
          data = await response.json();
        } catch {
          // If response is not JSON, return empty object
          data = {} as T;
        }

        return {
          data,
          status: response.status,
          headers,
        };
      } catch (error) {
        if (error instanceof ApiClientError) {
          throw error;
        }

        // Handle network errors, timeouts, etc.
        throw new ApiClientError({
          message: error instanceof Error ? error.message : 'Network error occurred',
          status: 0,
          details: { originalError: error },
        });
      }
    };

    return withRetry(
      operation,
      mergedOptions.retries || this.defaultOptions.retries!,
      mergedOptions.retryDelay || this.defaultOptions.retryDelay!
    );
  }

  // Public methods for different HTTP verbs
  public async get<T>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, 'GET', undefined, options);
  }

  public async post<T>(endpoint: string, body?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, 'POST', body, options);
  }

  public async put<T>(endpoint: string, body?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, 'PUT', body, options);
  }

  public async delete<T>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, 'DELETE', undefined, options);
  }
}

// Export singleton instance
export const apiClient = new ApiClient(); 